import { useState, useRef, useCallback } from "react";
import { logger } from "@origin-digital/reporting-client";
import { fetchSearchAi } from "../services/searchAi";

const LOG_PREFIX = "[ConvoSearch]";

/**
 * Upper bound on how long the answer panel will wait for `/api/search-ai`.
 *
 * The BFF allows the upstream Squiz conversational call 30s (`squizConvo.timeoutMs`),
 * so this sits below that ceiling: a request still running at 20s is one the BFF is
 * itself about to abandon. Without a bound the panel stays in `connecting` forever
 * and the skeleton keeps the real keyword results pushed down the page, which
 * degrades search for everyone who never asked for an AI answer.
 *
 * The generated contract client takes no AbortSignal, so the in-flight request is
 * not cancelled — this stops the UI waiting on it. The `requestId` guard already
 * discards a late resolution.
 */
export const CONVO_SEARCH_TIMEOUT_MS = 20_000;

class ConvoSearchTimeoutError extends Error {
  constructor() {
    super(`timed out after ${CONVO_SEARCH_TIMEOUT_MS}ms`);
    this.name = "ConvoSearchTimeoutError";
  }
}

export interface ConvoSource {
  url: string;
  title: string;
}

export type ConvoDecision = "TRIGGER" | "BLOCKED" | "NEUTRAL";

export interface ConvoState {
  status: "idle" | "connecting" | "done" | "error";
  decision: ConvoDecision | null;
  question: string;
  answer: string;
  sources: ConvoSource[];
  error: string | null;
  threadId: string | null;
  messageId: string | null;
}

const idle: ConvoState = {
  status: "idle",
  decision: null,
  question: "",
  answer: "",
  sources: [],
  error: null,
  threadId: null,
  messageId: null,
};

/**
 * Fetches a single AI answer for a search query from the BFF REST endpoint
 * (`/api/search-ai`). The BFF brokers the upstream Squiz conversational search and
 * returns one consolidated answer — there is no streaming or multi-turn thread.
 */
export function useConvoSearch() {
  const [state, setState] = useState<ConvoState>({ ...idle });
  const requestIdRef = useRef(0);

  const reset = useCallback(() => {
    requestIdRef.current += 1;
    setState({ ...idle });
  }, []);

  const search = useCallback(async (query: string) => {
    const trimmed = query.trim();
    if (!trimmed) {
      return;
    }

    const requestId = (requestIdRef.current += 1);
    setState({
      status: "connecting",
      decision: null,
      question: trimmed,
      answer: "",
      sources: [],
      error: null,
      threadId: null,
      messageId: null,
    });

    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    try {
      const { data } = await Promise.race([
        fetchSearchAi(trimmed),
        new Promise<never>((_resolve, reject) => {
          timeoutId = setTimeout(
            () => reject(new ConvoSearchTimeoutError()),
            CONVO_SEARCH_TIMEOUT_MS
          );
        }),
      ]);
      if (requestId !== requestIdRef.current) {
        return;
      }
      setState({
        status: "done",
        decision: data.decision,
        question: trimmed,
        answer: data.answer ?? "",
        sources: data.sources ?? [],
        error: null,
        threadId: data.threadId ?? null,
        messageId: data.messageId ?? null,
      });
    } catch (error) {
      // Deliberately logs the length rather than the query itself. The BFF
      // hard-stop list exists because people type things like "suicidal" and
      // "financial hardship" into help search, so the raw text must not reach
      // New Relic. The BFF logs `queryLength` for the same reason.
      void logger.error(
        `${LOG_PREFIX} search failed — queryLength: ${
          trimmed.length
        } | ${String(error)}`
      );
      if (requestId !== requestIdRef.current) {
        return;
      }
      setState((s) => ({
        ...s,
        status: "error",
        error: "Something went wrong. Please try again.",
      }));
    } finally {
      clearTimeout(timeoutId);
    }
  }, []);

  return { state, search, reset };
}
